import { createTheme, ThemeOptions } from '@mui/material'
import type {} from '@mui/x-data-grid/themeAugmentation'
import type {} from '@mui/lab/themeAugmentation'
import type {} from '@mui/x-date-pickers/themeAugmentation'
import { CheckboxIcon } from '@/components/icons/CheckboxIcon'
// import { GridColumnMenu, GridColumnMenuProps } from '@mui/x-data-grid'

import React from 'react'

declare module '@mui/material/styles' {
  interface Palette {
    tertiary: Palette['primary']
  }

  interface PaletteOptions {
    tertiary?: PaletteOptions['primary']
  }

  interface TypeText {
    grey?: string
  }

  interface TypographyVariants {
    body3: React.CSSProperties
    // normal text bold
    body4: React.CSSProperties
    body3Link: React.CSSProperties
    caption2: React.CSSProperties
  }

  // allow configuration using `createTheme()`
  interface TypographyVariantsOptions {
    body3?: React.CSSProperties
    body4?: React.CSSProperties
    body3Link?: React.CSSProperties
    caption2?: React.CSSProperties
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    body3: true
    body4: true
    body3Link: true
    caption2: true
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    tertiary: true
  }
}

// const CustomGridColumnMenu = (props: GridColumnMenuProps) => {
//   return <GridColumnMenu {...props} slots={{}} slotProps={{}} />
// }

const palette: ThemeOptions['palette'] = {
  primary: {
    main: '#001DE0',
    light: '#D3E8FF',
    contrastText: '#F8F7F8',
  },
  secondary: {
    main: '#F9BD2A',
    light: '#FDF2C8',
    contrastText: '#07135F',
  },
  tertiary: {
    main: '#FF44A6',
    light: '#FEE5F4',
    contrastText: '#F8F7F8',
  },
  error: {
    main: '#D71E30',
    light: '#FFE1E4',
    contrastText: '#F8F7F8',
  },
  success: {
    main: '#00864D',
    light: '#D3FFED',
    contrastText: '#F8F7F8',
  },
  text: {
    primary: '#07135F',
    grey: '#6B7085',
  },
  DataGrid: {
    bg: '#FEFEFF',
    headerBg: '#F8F7F8',
  },
}

const defaultText = {
  fontSize: '14px',
  lineHeight: 1.5,
  fontWeight: 400,
  fontFamily: ['Mulish', 'sans-serif'].join(','),
}

export const theme = createTheme({
  typography: {
    fontFamily: ['Mulish', 'sans-serif'].join(','),
    h1: {
      fontSize: '57px',
      lineHeight: '64px',
      fontWeight: 800,
    },
    h2: {
      fontSize: '45px',
      lineHeight: '52px',
      fontWeight: 800,
    },
    h3: {
      fontSize: '36px',
      lineHeight: '44px',
      fontWeight: 800,
    },
    h4: {
      fontSize: '32px',
      lineHeight: '40px',
      fontWeight: 800,
    },
    h5: {
      fontSize: '28px',
      lineHeight: '36px',
      fontWeight: 800,
    },
    h6: {
      fontSize: '24px',
      lineHeight: '32px',
      fontWeight: 800,
    },
    // NOTE: h7 variant in design system
    subtitle1: {
      fontSize: '20px',
      lineHeight: '30px',
      fontWeight: 800,
    },
    // NOTE: text large variant in design system
    subtitle2: {
      fontSize: '18px',
      lineHeight: '24px',
      fontWeight: 700,
    },
    body1: {
      fontSize: '16px',
      lineHeight: '22px',
      fontWeight: 700,
    },
    body2: {
      fontSize: '16px',
      lineHeight: '24px',
      fontWeight: 400,
    },
    body3: defaultText,
    body4: { ...defaultText, fontWeight: 700 },
    body3Link: {
      ...defaultText,
      color: '#001DE0',
      cursor: 'pointer',
    },
    caption: {
      fontSize: '12px',
      lineHeight: '16px',
      fontWeight: 400,
    },
    caption2: {
      fontSize: '12px',
      lineHeight: 1.3,
      fontWeight: 600,
    },
  },
  palette,
  components: {
    MuiTypography: {
      defaultProps: {
        variant: 'body3',
        variantMapping: {
          h1: 'h1',
          h2: 'h2',
          h3: 'h3',
          h4: 'h4',
          h5: 'h5',
          h6: 'h6',
          subtitle1: 'h5',
          subtitle2: 'h5',
          body1: 'h3',
          body2: 'h4',
          body3: 'p',
          body4: 'p',
          caption: 'p',
          caption2: 'p',
        },
      },
    },

    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'unset',
          borderRadius: '8px',
          padding: '10px 16px',
          // fontFamily: ['Inter', 'sans-serif'].join(','),
          lineHeight: 1,
        },
      },
      variants: [
        {
          props: { variant: 'text' },
          style: {
            fontSize: '14px',
            fontWeight: 600,
            padding: '8px',
          },
        },
      ],
    },
    MuiChip: {
      variants: [
        {
          props: { variant: 'filled' },
          style: {
            fontSize: '12px',
            fontWeight: 600,
            padding: '4px',
            color: '#0923E6',
            backgroundColor: '#ECEFF7',
          },
        },
      ],
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          ...defaultText,
        },
        input: {
          ...defaultText,
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
        size: 'small',
        fullWidth: true,
      },
      styleOverrides: {
        root: {
          ...defaultText,
        },
      },
    },
    MuiSelect: {
      defaultProps: {
        // fullWidth: true,
        size: 'small',
      },
      styleOverrides: {
        root: {
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(0, 0, 0, 0.23)',
            borderWidth: '1px',
          },
          ...defaultText,
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          ...defaultText,
        },
        option: {
          ...defaultText,
        },
        noOptions: {
          ...defaultText,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          ...defaultText,
        },
      },
    },
    MuiCheckbox: {
      defaultProps: {
        checkedIcon: React.createElement(CheckboxIcon, { check: true }),
        icon: React.createElement(CheckboxIcon, { check: false }),
        indeterminateIcon: React.createElement(CheckboxIcon, {
          indeterminate: true,
        }),
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        label: {
          ...defaultText,
        },
        root: { margin: 0 },
      },
    },
    MuiListItemText: {
      defaultProps: {
        slotProps: {
          primary: {
            variant: 'body3',
          },
        },
      },
    },
    MuiPaper: {
      defaultProps: {
        elevation: 8,
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        selectLabel: {
          ...defaultText,
        },
        displayedRows: {
          ...defaultText,
        },
        actions: {
          ...defaultText,
        },
        select: {
          padding: 0,
        },
      },
    },

    MuiDataGrid: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
        },
        columnHeader: {
          ...defaultText,
        },
        cell: {
          ...defaultText,
          wordWrap: 'break-word',
          overflow: 'visible',
          padding: '8px',
          maxHeight: 'unset',
        },
        overlay: {
          ...defaultText,
        },
        selectedRowCount: {
          ...defaultText,
        },
        cellCheckbox: {
          padding: '0',
        },
      },
      defaultProps: {
        autoHeight: false,
        disableColumnFilter: true,
        rowSelection: false,
        initialState: {
          pagination: {
            paginationModel: {
              pageSize: 10,
            },
          },
        },
        pageSizeOptions: [10, 15, 20, 25, 30, 50, 100],
        // FIXME: this is reason for runtime error: ResizeObserver loop completed with undelivered notifications.
        getRowHeight: () => 'auto',
      },
    },
    MuiTabPanel: {
      styleOverrides: {
        root: {
          paddingLeft: '0px',
          paddingRight: '0px',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: { fontWeight: 700, textTransform: 'none' },
      },
    },
    // NOTE: MUI Date Pickers - Start
    MuiPickersInputBase: {
      styleOverrides: {
        root: {
          fontSize: '14px',
          fontWeight: 400,
          '& .MuiInputBase-input': {
            padding: '8px',
          },
        },
      },
    },
    MuiDateField: {
      defaultProps: {
        size: 'small',
        format: 'DD/MM/YYYY',
      },
    },
    // NOTE: MUI Date Pickers - End
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: '#F8F7F8',
        },
      },
    },
  },
})
